import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideMemberDetail: React.FC = () => {
  return (
    <GuidePageLayout
      title="要員の詳細情報を確認する"
      description="要員の登録内容を詳細に確認します。"
      breadcrumbs={[{ label: '要員管理', path: '/guide/member/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '要員管理画面を開く' },
            { label: '要員一覧から対象要員の行をクリックする' },
            {
              label: '「要員詳細」が右パネルに表示される',
              description:
                '要員の全情報が参照専用で表示されます。ヘッダー右上にIDとコピーボタンが表示されます。',
              content: (
                <GuideMockContainer label="要員詳細（参照モード）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        要員詳細
                      </Typography>
                      <GuideMockChip label="社員" color="primary" />
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: xxxxxxxx-xxxx-xxxx-xxxx
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="表示される情報">
        <GuideTable
          headers={['項目', '表示条件']}
          rows={[
            ['要員名 / カナ', '常に表示'],
            ['雇用形態 / 性別', '常に表示'],
            ['契約形態', '社外の場合のみ'],
            ['パートナー名', '社外の場合のみ'],
            ['個人事業主', '社外の場合のみ'],
            ['ユーザー（メールアドレス）', '社員の場合のみ'],
            ['所属', '社員の場合のみ'],
            ['登録者 / 登録日', '常に表示'],
            ['備考', '常に表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', '要員IDをクリップボードにコピー'],
            ['編集ボタン', '編集モードに切り替える（削除済みの場合は非表示）'],
            [
              '削除ボタン',
              '要員を削除する（削除済みでない場合のみ表示）。確認ダイアログで確認後に実行',
            ],
          ]}
        />
        <Stack sx={{ mt: 1 }}>
          <GuideMockContainer label="フッターボタン（削除済みでない場合のみ表示）">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <GuideMockButton label="削除" color="error" startIcon={<DeleteIcon />} />
              <GuideMockButton label="編集" startIcon={<EditIcon />} />
            </Stack>
          </GuideMockContainer>
        </Stack>
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>削除済みの要員は参照のみ可能で、編集ボタンは表示されません。</GuideInfoBox>
          <GuideInfoBox>
            登録者が削除済みユーザーの場合、登録者名の代わりにユーザーIDが表示されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideMemberDetail;
